<template lang="pug">
#send-document-list(v-cloak)
  .box
    .box-header.d-flex.flex-row.flex-wrap
      .note
        span ご家族や施設関係者のLINEにPDF文書を一斉送信できます（ご家族の場合、利用者ごとに異なる文書も一斉送信できます）
        br
        span ご家族や施設関係者はLINEから同意の返信をすることができ、施設側では同意返信の集計とファイル出力ができます。
      .flex-fill.d-flex.flex-row-reverse
        button.btn.add-btn(@click="gotoSendDocumentRegister" type="button") 新規作成
        button.btn.template-btn(v-if="_showTemplate" @click="gotoTemplateList" type="button") テンプレート
        button.btn.category-btn(@click="gotoCategoryList" type="button") 文書種類
    .box-body
      .history-period.d-flex.flex-row.align-items-center
        .title.mr-2 履歴範囲
        b-form-select.history-option#history-period(v-model="historyPeriod" :options="historyPeriodOptions" size="md" @change="changeHistoryPeriod")
      .mb-2
      SendDocumentTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onChangePublic="onChangePublic"
        @onDetail="gotoSendDocumentDetail"
        @onEdit="gotoSendDocumentEdit"
        @onDelete="showSendDocumentDeleteModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・宛先
              div - 利用者名
              div - 利用者名（ふりがな）
              div - 部屋名
              div - 家族ユーザー名
              div ・タイトル
              div ※ 利用者情報にふりがなを登録前の文書送付はふりがな検索ができません。
        // フィルタリング
        span.table-option-text 文書種類:
        .filter
          b-form-select(
            id="filter-input"
            v-model="filter.categoryId"
            :options="filterOptions"
            size="md"
          )
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  OkCancelModal(v-model="showChangePublicModal" :title="changePublicModalMessage" @ok="changePublic(changeTargetSendDocument)")

  OkCancelModal(v-model="showDeleteSendDocumentModal" :title="deleteSendDocumentMessage" @ok="deleteSendDocument")
  CheckDeleteModal(v-model="showCheckDeleteModal" :title="deleteSendDocumentMessage" @ok="deleteSendDocument")
    template(#message)
      div 履歴からは削除されますが、受信者のメール・LINEトーク画面からは削除することができません。
      div.mb-2 よろしいですか？
  OkModal(v-model="showDeleteSendDocumentSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import SendDocumentsService, {
  SendDocumentFamilyReceiver,
  SendDocumentGetCountQuery,
  SendDocumentGetModel,
  SendDocumentGetsQuery,
  SendDocumentOtherUserReceiver,
  SendDocumentPatchModel,
  SendDocumentReplyGetModel,
  Type
} from "@/commons/services/SendDocuments";
import SendDocumentCategoriesService, { SendDocumentCategoryGetModel } from "@/commons/services/SendDocumentCategories";
import { GroupType } from "@/commons/services/SendMessageGroups";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import SendDocumentTable, { FilterSetting, SendDocumentTableField, SendDocumentTableItem } from "@/pc/containers/sendDocuments/SendDocumentTable.vue";
import CheckDeleteModal from "@/pc/components/modals/CheckDeleteModal.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 文書送付一覧画面コンポーネント
 */
@Component({
  components: {
    CheckDeleteModal,
    OkCancelModal,
    OkModal,
    PageLoading,
    HelpIcon,
    SendDocumentTable
  }
})
export default class SendDocumentList extends Vue {
  /** 文書送付サービス */
  @Inject("SendDocumentsService") sendDocumentsService!: SendDocumentsService;
  /** 文書送付カテゴリーサービス */
  @Inject("SendDocumentCategoriesService") categoriesService!: SendDocumentCategoriesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 公開状態変更確認モーダル表示可否 */
  showChangePublicModal: boolean = false;
  /** 公開状態変更確認モーダルメッセージ */
  changePublicModalMessage: string = "";
  /** 公開状態変更対象の文書送付情報 */
  changeTargetSendDocument: SendDocumentTableItem | null = null;
  /** 削除確認モーダル表示可否 */
  showDeleteSendDocumentModal: boolean = false;
  /** チェックボックス付き削除確認モーダル表示可否 */
  showCheckDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSendDocumentSuccessModal: boolean = false;

  /** 履歴範囲 */
  historyPeriod: number = HistoryPeriodOptionValue.ONE_MONTH;
  /** 履歴範囲選択肢 */
  historyPeriodOptions: Array<{ value: number | string; text: string }> = [
    { value: HistoryPeriodOptionValue.ONE_MONTH, text: "1ヵ月" },
    { value: HistoryPeriodOptionValue.THREE_MONTH, text: "3ヵ月" },
    { value: HistoryPeriodOptionValue.SIX_MONTH, text: "6ヵ月" },
    { value: HistoryPeriodOptionValue.ONE_YEAR, text: "1年" },
    { value: HistoryPeriodOptionValue.ALL, text: "全て" }
  ];

  /** テーブル列名 */
  fields: Array<SendDocumentTableField> = [];
  /** テーブル各行の情報 */
  items: Array<SendDocumentTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** カテゴリー名のMAP key:カテゴリーID value:カテゴリー名 */
  categoryNameMap: Map<number, string> = new Map<number, string>();

  /** 削除対象の文書送付ID */
  deleteTargetSendDocumentId: number | null = null;
  /** 削除モーダルに表示するメッセージ */
  deleteSendDocumentMessage: string = "";

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", categoryId: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendDocument.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendDocument.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendDocument.pageRange;

  /** エラーメッセージ */
  errorMessage: string = "";

  get _showTemplate(): boolean {
    return userStore.loginUser.permissions.sendDocumentsFunction.useTemplate;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-document-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendDocumentTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    // カテゴリー名変換のためにカテゴリーを取得する
    const sendDocumentCategories: Array<SendDocumentCategoryGetModel> = await this.getSendDocumentCategories();
    const categoryNameMap: Map<number, string> = this.createCategoryNameMap(sendDocumentCategories);
    this.categoryNameMap = categoryNameMap;

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(sendDocumentCategories);
    this.filterOptions = filterOptions;

    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<SendDocumentTableField> {
    let fields: Array<SendDocumentTableField> = [];

    if (userStore.loginUser.permissions.sendDocumentsFunction.useDraft) {
      fields.push({ key: "isSended", label: "送付状態", sortable: false, sortDirection: "desc", class: "is-sended text-center" });
    }
    if (userStore.loginUser.permissions.sendDocumentsFunction.useControl) {
      fields.push({ key: "isPublic", label: "公開状態", sortable: false, sortDirection: "desc", class: "is-public text-center" });
    } else {
      fields.push({ key: "isPublicText", label: "公開状態", sortable: false, sortDirection: "desc", class: "is-public text-center" });
    }
    fields.push({ key: "sendDatetime", label: "送付日時", sortable: false, sortDirection: "desc", class: "send-datetime text-center" });
    fields.push({ key: "receivers", label: "宛先", sortable: false, sortDirection: "desc", class: "receivers" });
    fields.push({ key: "category", label: "文書種類", sortable: false, sortDirection: "desc", class: "category" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "option", label: "オプション", sortable: false, sortDirection: "desc", class: "option text-center" });
    fields.push({ key: "unit", label: "文書単位", sortable: false, sortDirection: "desc", class: "unit text-center" });
    if (userStore.loginUser.permissions.sendDocumentsFunction.useControl) {
      fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });
    } else {
      fields.push({ key: "noOperation", label: "登録情報", sortable: false, sortDirection: "desc", class: "no-operation text-center" });
    }

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-document-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    // 履歴範囲情報がstoreに存在する場合は復元する
    if (this.$options.name && itemListFilterStore.historyPeriodMap[this.$options.name] != undefined) {
      this.historyPeriod = itemListFilterStore.historyPeriodMap[this.$options.name];
    }
    itemListFilterStore.initializeStateForUrl({ url: this.$options.name as string });
    return;
  }

  /**
   * ### 文書送付カテゴリーを取得する
   */
  public async getSendDocumentCategories(): Promise<Array<SendDocumentCategoryGetModel>> {
    const response: Array<SendDocumentCategoryGetModel> = await this.categoriesService.gets(userStore);
    return response;
  }

  /**
   * ### 文書送付カテゴリー名のMAPを作成する
   */
  public createCategoryNameMap(sendDocumentCategories: Array<SendDocumentCategoryGetModel>): Map<number, string> {
    let categoryNameMap: Map<number, string> = new Map<number, string>();

    for (const sendDocumentCategory of sendDocumentCategories) {
      categoryNameMap.set(sendDocumentCategory.id, sendDocumentCategory.name);
    }

    return categoryNameMap;
  }

  /**
   * ### 文書送付カテゴリーフィルター用選択項目
   */
  public createFilterOptions(sendDocumentCategories: Array<SendDocumentCategoryGetModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    for (const sendDocumentCategory of sendDocumentCategories) {
      filterOptions.push({
        value: `${sendDocumentCategory.id}`,
        text: sendDocumentCategory.name
      });
    }

    return filterOptions;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 履歴範囲の値に応じて履歴の取得範囲を設定する
    let startAndEnd: { start: string; end: string } | undefined = undefined;
    if (this.historyPeriod == HistoryPeriodOptionValue.ALL) {
      startAndEnd = undefined;
    } else {
      const today: Date = new Date();
      const end: string = this.$dateUtil.convertDateToString(today, "yyyymmdd235959000");
      today.setMonth(today.getMonth() - this.historyPeriod);
      const start: string = this.$dateUtil.convertDateToString(today, "yyyymmdd000000000");
      startAndEnd = { start, end };
    }

    // 全体の行数を取得する
    await this.setRows(startAndEnd);
    // テーブルに渡す各プロパティを作成する
    const sendDocuments: Array<SendDocumentGetModel> = await this.getSendDocuments(startAndEnd);
    const items: Array<SendDocumentTableItem> = this.createItems(sendDocuments, this.categoryNameMap);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    // 現在ページがstoreに存在する場合は復元する
    if (this.$options.name && itemListFilterStore.currentPageMap[this.$options.name] != undefined) {
      this.currentPage = itemListFilterStore.currentPageMap[this.$options.name];
    }

    this.isLoading = false;

    // 画面表示後、同意取得人数or確認取得人数を取得する
    // DBのスループット上限問題、chromeのリクエスト上限を回避するために50件ずつ取得し、取得する毎に1秒インターバルを挟む
    let promises: Array<Promise<void>> = [];
    for (const item of this.items) {
      if (item.needAgreement == true || item.needConfirm == true) {
        promises.push(this.setReplies(item));
      }

      if (Setting.requestLimit < promises.length) {
        const results: Array<any> = await Promise.all(promises);
        // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
        for (const result of results) {
          if (result instanceof SystemError) {
            errorHandle(result, this);
          }
        }
        promises = [];
        await this.$timerUtil.setTimeout(1000);
      }
    }
    let results: Array<any> = await Promise.all(promises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(params: { start: string; end: string } | undefined) {
    try {
      const queryParams: SendDocumentGetCountQuery = {
        onlyParent: true,
        onlySended: !userStore.loginUser.permissions.sendDocumentsFunction.useDraft
      };
      if (params) {
        queryParams.start = params.start;
        queryParams.end = params.end;
      }
      const count: number = await this.sendDocumentsService.getCount(userStore, queryParams);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 文書送付を取得する
   */
  public async getSendDocuments(params: { start: string; end: string } | undefined): Promise<Array<SendDocumentGetModel>> {
    let response: Array<SendDocumentGetModel> = [];

    try {
      const queryParams: SendDocumentGetsQuery = {
        onlyParent: true,
        onlySended: !userStore.loginUser.permissions.sendDocumentsFunction.useDraft
      };
      if (params) {
        queryParams.start = params.start;
        queryParams.end = params.end;
      }
      response = await this.sendDocumentsService.gets(userStore, queryParams);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const countQueryParams: SendDocumentGetCountQuery = {
          onlyParent: true,
          onlySended: !userStore.loginUser.permissions.sendDocumentsFunction.useDraft
        };
        const count: number = await this.sendDocumentsService.getCount(userStore, countQueryParams);

        const getQueryParams: SendDocumentGetsQuery = {
          onlyParent: true,
          onlySended: !userStore.loginUser.permissions.sendDocumentsFunction.useDraft
        };
        for (let i = 0; i < Math.ceil(count / 200); i++) {
          getQueryParams.startIndex = i * 200;
          getQueryParams.endIndex = i * 200 + 200;
          response = response.concat(await this.sendDocumentsService.gets(userStore, getQueryParams));
        }
      }
    }

    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(sendDocuments: Array<SendDocumentGetModel>, categoryNameMap: Map<number, string>): Array<SendDocumentTableItem> {
    let items: Array<SendDocumentTableItem> = [];

    for (const sendDocument of sendDocuments) {
      const categoryName: string | undefined = categoryNameMap.get(sendDocument.categoryId);
      items.push({
        ...sendDocument,
        categoryName: categoryName ? categoryName : "",
        replyedCount: null,
        nonreplyedCount: null
      });
    }

    return items;
  }

  /**
   * ### 同意／未同意、確認／未確認のモーダル表示のために返信情報を格納する
   */
  public setReplies(item: SendDocumentTableItem): Promise<void> {
    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const replies: Array<SendDocumentReplyGetModel> = await this.getReplies(item.mainId);

        switch (item.type) {
          case Type.ALL_FAMILY:
          case Type.SELECTED_FAMILY:
          case Type.RESIDENT_GROUP: {
            item.replyedCount = replies.length;

            const map: Map<string, string> = this.createFamilyReplyMap(replies);
            const nonReplyCount: number = this.getNonReplyFamiliesCount(item, map);
            item.nonreplyedCount = nonReplyCount;

            break;
          }
          case Type.SELECTED_OTHER_USER:
          case Type.OTHER_USER_GROUP: {
            item.replyedCount = replies.length;

            const map: Map<string, string> = this.createOtherUserReplyAndMap(replies);
            const nonReplyCount: number = this.getNonReplyOtherUserCount(item, map);
            item.nonreplyedCount = nonReplyCount;

            break;
          }
          case Type.SEND_MESSAGE_GROUP: {
            if (item.sendMessageGroupInfos == null) {
              let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${item.type}`);
              systemError.statusCode = 404;
              throw systemError;
            }

            if (item.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
              item.replyedCount = replies.length;

              const map: Map<string, string> = this.createFamilyReplyMap(replies);
              const nonReplyCount: number = this.getNonReplyFamiliesCount(item, map);
              item.nonreplyedCount = nonReplyCount;

              break;
            } else if (item.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
              item.replyedCount = replies.length;

              const map: Map<string, string> = this.createOtherUserReplyAndMap(replies);
              const nonReplyCount: number = this.getNonReplyOtherUserCount(item, map);
              item.nonreplyedCount = nonReplyCount;

              break;
            }
            break;
          }
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 文書送付の返信を取得する
   */
  public async getReplies(mainId: number): Promise<Array<SendDocumentReplyGetModel>> {
    const response: Array<SendDocumentReplyGetModel> = await this.sendDocumentsService.getReplies(userStore, mainId);
    return response;
  }

  /**
   * ### 家族の返信情報のMAPを作成する
   */
  public createFamilyReplyMap(replies: Array<SendDocumentReplyGetModel>): Map<string, string> {
    let map: Map<string, string> = new Map<string, string>();

    for (const reply of replies) {
      map.set(`${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`, `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`);
    }

    return map;
  }

  /**
   * ### 家族の未返信数を取得する
   */
  public getNonReplyFamiliesCount(sendDocument: SendDocumentGetModel, map: Map<string, string>): number {
    let count: number = 0;

    for (const receiver of sendDocument.receivers as Array<SendDocumentFamilyReceiver>) {
      const replyed: string | undefined = map.get(receiver.residentsSortKey);
      if (replyed != undefined) {
        continue;
      }

      count++;
    }

    return count;
  }

  /**
   * ### 施設関係者の返信情報のMAPを作成する
   */
  public createOtherUserReplyAndMap(replies: Array<SendDocumentReplyGetModel>): Map<string, string> {
    let map: Map<string, string> = new Map<string, string>();

    for (const reply of replies) {
      map.set(`${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`, `${reply.sortKeyPartOne}_${reply.sortKeyPartTwo}`);
    }

    return map;
  }

  /**
   * ### 施設関係者の未返信数を取得する
   */
  public getNonReplyOtherUserCount(sendDocument: SendDocumentGetModel, map: Map<string, string>): number {
    let count: number = 0;

    for (const receiver of sendDocument.receivers as Array<SendDocumentOtherUserReceiver>) {
      const replyed: string | undefined = map.get(receiver.otherUserGroupsSortKey);
      if (replyed != undefined) {
        continue;
      }

      count++;
    }

    return count;
  }

  /**
   * ### 期間変更のセレクトボックス値変更イベント
   *
   * - 履歴を取得しなおす
   */
  public async changeHistoryPeriod(): Promise<void> {
    this.isLoading = true;
    // 更新による画面の再描画
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1;
    await this.createView();
    this.isLoading = false;
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 文書送付登録画面へ遷移する
   */
  public gotoSendDocumentRegister(): void {
    this.$router.push(PcUrl.SendDocumentRegister);
    return;
  }

  /**
   * ### 文書送付カテゴリー一覧画面へ遷移する
   */
  public gotoCategoryList(): void {
    this.$router.push(PcUrl.SendDocumentCategoryList);
    return;
  }

  /**
   * ### 文書送付テンプレート一覧画面へ遷移する
   */
  public gotoTemplateList(): void {
    this.$router.push(PcUrl.SendDocumentTemplateList);
    return;
  }

  /**
   * ### 文書送付の公開・非公開変更ダイアログを表示する
   *
   * - 公開・非公開で表示するメッセージを分岐する
   * - 更新対象を保持する
   */
  public async onChangePublic(item: SendDocumentTableItem): Promise<void> {
    if (item.isPublic) {
      this.changePublicModalMessage = `${item.title}を非公開にしますか？`;
    } else {
      this.changePublicModalMessage = `${item.title}を公開しますか？`;
    }

    this.changeTargetSendDocument = item;
    this.showChangePublicModal = true;

    return;
  }

  /**
   * ### 文書送付の公開／非公開を変更する
   */
  public async changePublic(item: SendDocumentTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      item.isPublic = !item.isPublic;
      const requestBody: SendDocumentPatchModel = this.createSendDocumentRequestBody(item);
      await this.sendDocumentsService.patch(userStore, item.mainId, requestBody);

      // 更新による画面の再描画
      this.items = [];
      this.currentRows = this.items.length;
      this.currentPage = 1;

      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 文書送付を更新するためのリクエストボディを作成する
   */
  public createSendDocumentRequestBody(item: SendDocumentTableItem): SendDocumentPatchModel {
    let receivers: {
      receivers: string[];
      receiversByLine: string[];
      receiversByMail: string[];
    } | null = null;
    switch (item.type) {
      case Type.ALL_FAMILY:
      case Type.SELECTED_FAMILY: {
        receivers = this.createFamilyReceivers(item);
        break;
      }
      case Type.SELECTED_OTHER_USER: {
        receivers = this.createOtherUserReceivers(item);
        break;
      }
      case Type.SEND_MESSAGE_GROUP: {
        if (item.sendMessageGroupInfos == null) {
          let systemError: SystemError = new SystemError(`sendMessageGroupInfos is null. type:${item.type}`);
          systemError.statusCode = 404;
          throw systemError;
        }

        if (item.sendMessageGroupInfos[0].type == GroupType.FAMILY) {
          receivers = this.createFamilyReceivers(item);
          break;
        } else if (item.sendMessageGroupInfos[0].type == GroupType.OTHER_USER) {
          receivers = this.createOtherUserReceivers(item);
          break;
        }
      }
    }
    // ありえないが、receiversが作られなかったらシステムエラー
    if (receivers == null) {
      let systemError: SystemError = new SystemError(`SendDocumentList.createSendDocumentRequestBody() receivers is null. ${JSON.stringify(item)}`);
      systemError.statusCode = 400;
      throw systemError;
    }

    const requestBody: SendDocumentPatchModel = {
      isPublic: item.isPublic,
      type: item.type,
      receivers: receivers.receivers,
      receiversByLine: receivers.receiversByLine ? receivers.receiversByLine : [],
      receiversByMail: receivers.receiversByMail ? receivers.receiversByMail : [],
      sendMessageGroupInfos: item.sendMessageGroupInfos,
      residentGroupInfos: item.residentGroupInfos,
      otherUserGroupInfos: item.otherUserGroupInfos,
      unit: item.unit,
      categoryId: item.categoryId,
      title: item.title,
      text: item.text,
      needConfirm: item.needConfirm,
      needAgreement: item.needAgreement,
      fileUrls: item.fileUrls,
      updated: item.updated
    };

    return requestBody;
  }

  /**
   * ### 家族のreceiversを作成する
   */
  public createFamilyReceivers(item: SendDocumentTableItem): {
    receivers: string[];
    receiversByLine: string[];
    receiversByMail: string[];
  } {
    const receivers: string[] = [];
    const receiversByLine: string[] = [];
    const receiversByMail: string[] = [];

    for (const receiver of item.receivers) {
      const _receiver = receiver as SendDocumentFamilyReceiver;
      receivers.push(_receiver.residentsSortKey);
    }
    for (const receiver of item.receiversByLine) {
      const _receiver = receiver as SendDocumentFamilyReceiver;
      receiversByLine.push(_receiver.residentsSortKey);
    }
    for (const receiver of item.receiversByMail) {
      const _receiver = receiver as SendDocumentFamilyReceiver;
      receiversByMail.push(_receiver.residentsSortKey);
    }
    return { receivers, receiversByLine, receiversByMail };
  }

  /**
   * ### 施設関係者のreceiversを作成する
   */
  public createOtherUserReceivers(item: SendDocumentTableItem): {
    receivers: string[];
    receiversByLine: string[];
    receiversByMail: string[];
  } {
    const receivers: string[] = [];
    const receiversByLine: string[] = [];
    const receiversByMail: string[] = [];

    for (const receiver of item.receivers) {
      const _receiver = receiver as SendDocumentOtherUserReceiver;
      receivers.push(_receiver.otherUserGroupsSortKey);
    }
    for (const receiver of item.receiversByLine) {
      const _receiver = receiver as SendDocumentOtherUserReceiver;
      receiversByLine.push(_receiver.otherUserGroupsSortKey);
    }
    for (const receiver of item.receiversByMail) {
      const _receiver = receiver as SendDocumentOtherUserReceiver;
      receiversByMail.push(_receiver.otherUserGroupsSortKey);
    }
    return { receivers, receiversByLine, receiversByMail };
  }

  /**
   * ### 文書送付詳細画面へ遷移する
   */
  public gotoSendDocumentDetail(item: SendDocumentTableItem): void {
    this.$router.push({ name: PcUrl.SendDocumentDetail, params: { id: `${item.mainId}` } });
    return;
  }

  /**
   * ### 文書送付編集画面へ遷移する
   */
  public gotoSendDocumentEdit(item: SendDocumentTableItem): void {
    this.$router.push({ name: PcUrl.SendDocumentEdit, params: { id: `${item.mainId}` } });
    return;
  }

  /**
   * ### 文書送付の削除確認モーダルを表示する
   *
   * - 送信済みの場合はチェックボックス付きモーダルを表示する
   * - 未送信の場合は通常の削除モーダルを表示する
   */
  public showSendDocumentDeleteModal(sendDocument: SendDocumentTableItem): void {
    this.deleteTargetSendDocumentId = sendDocument.mainId;

    this.deleteSendDocumentMessage = `${sendDocument.title ? sendDocument.title : "タイトル未入力"}を削除しますか？`;
    if (sendDocument.isSended) {
      this.showCheckDeleteModal = true;
    } else {
      this.showDeleteSendDocumentModal = true;
    }

    return;
  }

  /**
   * ### 文書送付を削除する
   */
  public async deleteSendDocument(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.sendDocumentsService.delete(userStore, this.deleteTargetSendDocumentId as number);
    this.showDeleteSendDocumentSuccessModal = true;

    // 削除による画面の再描画
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1;

    await this.createView();
    this.isBusy = false;

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
      itemListFilterStore.setCurrentPage({ url: this.$options.name, currentPage: this.currentPage });
      itemListFilterStore.setHistoryPeriod({ url: this.$options.name, historyPeriod: this.historyPeriod });
    }
    next();
  }
}

export enum HistoryPeriodOptionValue {
  UNKNOWN = -1,
  ONE_MONTH = 1,
  THREE_MONTH = 3,
  SIX_MONTH = 6,
  ONE_YEAR = 12,
  ALL = 0
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#send-document-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    width: 100%;

    .add-btn,
    .category-btn {
      width: 100px;
      min-width: 100px;
    }
    .category-btn,
    .template-btn {
      margin-right: 8px;
    }
  }

  .history-period {
    .title {
      font-weight: bold;
    }
    .history-option {
      width: 120px;
    }
  }
}
</style>
